/**
 * Lead Type - ESTRUCTURA CONSOLIDADA
 * Incluye campos de owly-api + AI scoring + tracking avanzado
 */

export enum LeadStatus {
  NEW = 'new',
  CONTACTED = 'contacted',
  QUALIFIED = 'qualified',
  PROPOSAL = 'proposal',
  NEGOTIATION = 'negotiation',
  CLOSED_WON = 'closed_won',
  CLOSED_LOST = 'closed_lost',
  NURTURING = 'nurturing'
}

export enum LeadSource {
  WEBSITE = 'website',
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
  WHATSAPP = 'whatsapp',
  REFERRAL = 'referral',
  COLD_CALL = 'cold_call',
  TRADE_SHOW = 'trade_show',
  PARTNER = 'partner',
  ORBIT_VIEW = 'orbit_view',
  OTHER = 'other'
}

// ========================================
// LEAD SOURCE TAXONOMY (Fase 2)
// ========================================

export type IntegrationStatus = 'active' | 'expired' | 'revoked' | 'error' | 'pending' | 'not_configured' | null;

/**
 * High-level availability state for a LeadSource. Drives the main badge in
 * the lead-sources UI (vs `integration_status` which is the granular
 * connection-health sub-state).
 *
 *   connected    → provider exists, IntegrationConnection present (any sub-state)
 *   available    → active provider, no connection yet for this company → CTA "Conectar"
 *   beta         → provider flagged is_beta or is_active=False → CTA "Conectar (Beta)"
 *   coming_soon  → no provider yet but on the roadmap → CTA "Notificarme cuando esté listo"
 *   manual_only  → no provider needed (presential, referrals, traditional) → no CTA
 */
/**
 * Method this source uses to receive leads automatically.
 *
 *   integration  → has an IntegrationProvider (Facebook, Google Ads, WhatsApp).
 *                  UI: full OAuth/connection flow.
 *   partner_api  → real-estate portal that integrates via a PartnerAPIKey.
 *                  UI: lightweight "Conectar" modal that issues key + secret.
 *   planned      → on roadmap, no integration yet. UI: "Próximamente" / Notify-me.
 *   manual       → no integration by design (presential, referrals, ferias).
 *                  UI: no connect button.
 */
export type ConnectionMethod = 'integration' | 'partner_api' | 'planned' | 'manual';

export type AvailabilityStatus =
  | 'connected'
  | 'available'
  | 'beta'
  | 'coming_soon'
  | 'manual_only';

/**
 * A publisher/brand that operates one or more LeadSources across channels
 * (e.g. Estrenar Vivienda has portal + magazine; FincaRaíz has portal + events).
 * System publishers (is_system=true) are the global catalog. Custom ones are
 * per-company.
 */
export interface LeadSourcePublisher {
  id: string;
  key: string;
  name: string;
  description?: string;
  website_url?: string;
  logo_url?: string;
  is_system: boolean;
  is_active: boolean;
  sources_count?: number;
  sources?: Array<{
    id: string;
    key: string;
    name: string;
    category: string;
    category_label: string;
    is_system: boolean;
  }>;
  created_at?: string;
  updated_at?: string;
}

export interface LeadSourceType {
  id: string;
  key: string;
  name: string;
  description?: string;
  category: string;
  category_label: string;
  medium: string;
  detection_type: 'automatic' | 'manual';
  is_system: boolean;
  is_active: boolean;
  /** OWLY controls: is this source visible to the client? (from CompanyLeadSourceConfig) */
  config_is_enabled: boolean;
  /** Client controls: has the client opted out? (from CompanyLeadSourceConfig) */
  config_is_active: boolean;
  /** @deprecated Use config_is_enabled. Backward compat alias. */
  platform_enabled: boolean;
  project_is_active?: boolean | null;
  order: number;
  utm_source_match?: string;
  utm_medium_match?: string;
  leads_count?: number;
  integration_provider_code?: string | null;
  integration_status?: IntegrationStatus;
  /** High-level state — used by the badge in the UI. */
  availability_status?: AvailabilityStatus;
  /** What kind of connection this source supports. Drives which UI to show. */
  connection_method?: ConnectionMethod;
  /** Public-facing partner brand name (e.g. "FincaRaíz") — only set for partner_api sources. */
  partner_name?: string | null;
  /** True when the source maps to a provider flagged is_beta or is_active=false. */
  provider_is_beta?: boolean;
  /** True when this company has expressed interest in the integration. */
  has_interest?: boolean;
  /** ID of the LeadSourcePublisher this source belongs to (e.g. Estrenar Vivienda). */
  publisher_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export enum LeadPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent'
}

export enum DocumentType {
  CC = 'CC', // Cédula de Ciudadanía
  CE = 'CE', // Cédula de Extranjería
  TI = 'TI', // Tarjeta de Identidad
  PA = 'PA', // Pasaporte
  NIT = 'NIT',
  OTHER = 'other'
}

export interface LeadBudget {
  min: number;
  max: number;
  currency: string;
  financing: boolean;
  downPaymentPercentage?: number;
}

export interface LeadPreferences {
  propertyTypes: string[];
  locations: string[];
  minBedrooms?: number;
  minBathrooms?: number;
  minArea?: number;
  amenities?: string[];
  moveInDate?: string;
}

// NUEVO: Factores que afectan la conversión
export interface ConversionFactors {
  budgetMatch: number; // 0-100
  timelineUrgency: number; // 0-100
  engagementLevel: number; // 0-100
  responseRate: number; // 0-100
  decisionMakerAccess: boolean;
  competitorComparison: boolean;
}

// NUEVO: Insights generados por AI
export interface AIInsights {
  summary: string;
  strengths: string[];
  concerns: string[];
  recommendations: string[];
  nextBestAction: string;
  generatedAt: string;
}

export interface Lead {
  // Identificación
  id: string;
  leadNumber: string; // Ej: "LEAD-2024-001"

  // Información Personal
  firstName: string;
  lastName: string;
  first_name?: string;
  last_name?: string;
  email: string;
  alternateEmail?: string;
  alternate_email?: string;
  phone: string;
  alternatePhone?: string;
  alternate_phone?: string;
  company?: string;
  position?: string;

  // Género
  gender?: 'male' | 'female' | 'other';

  // Documento de Identidad
  documentType?: DocumentType;
  documentNumber?: string;
  document_type?: string;
  document_number?: string;

  // Estado y Tracking
  status: LeadStatus;
  priority: LeadPriority;
  source: LeadSource;
  sourceDetail?: string; // NUEVO: Granularidad adicional (ej: "Facebook Ad Campaign X")
  leadScore: number; // 0-100
  lead_score?: number;
  business_status?: string;
  businessStatus?: string;

  // 3D Scoring (V2)
  data_quality_score?: number; // 0-100
  profile_score?: number; // 0-100
  engagement_score?: number; // 0-100
  priority_matrix_label?: string; // e.g. "A1★", "B2", "C3"

  // Lead Source Taxonomy (Fase 2)
  lead_source?: string; // FK UUID
  lead_source_name?: string;
  lead_source_category?: string;
  lead_source_detail?: LeadSourceType;
  first_touch_source?: string;
  last_touch_source?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;

  // NUEVO: AI Scoring y Predictions
  ai_close_probability?: number; // 0-100
  ai_insights?: AIInsights;
  conversion_probability_factors?: ConversionFactors;

  // Asignación
  assignedTo?: string; // userId
  assignedDate?: string; // NUEVO: Tracking de cuándo se asignó
  assignedBy?: string; // userId que asignó

  // Interacción y Engagement
  lastContactDate?: string;
  lastInteractionDate?: string; // NUEVO: Cualquier interacción (email open, quote view, etc.)
  interactionCount?: number; // NUEVO: Total de interacciones
  nextFollowUpDate?: string;

  // Intereses y Preferencias
  interestedProjects?: string[]; // projectIds (multiple projects of interest)
  interestedUnits?: string[]; // unitIds
  budget?: LeadBudget;
  preferences?: LeadPreferences;

  // Financiero (snake_case from API)
  budget_min?: number;
  budget_max?: number;
  budgetMin?: number;
  budgetMax?: number;
  monthly_income?: number;
  monthlyIncome?: number;
  savings_amount?: number;
  savingsAmount?: number;
  severance_amount?: number;
  severanceAmount?: number;
  monthly_debt_amount?: number;
  employment_type?: string;
  employmentType?: string;
  down_payment_percentage?: number;
  downPaymentPercentage?: number;

  // ========================================
  // RELACIONES (Cross-Module References)
  // ========================================

  /** ID de la compañía a la que pertenece el lead (Many-to-One) */
  companyId?: string;

  /** ID del proyecto principal de interés (Many-to-One) */
  project?: string;
  projectId?: string;

  // Cotizaciones
  quoteCount?: number;
  totalQuoteValue?: number;
  lastQuoteDate?: string;

  // Deals
  deals_summary?: {
    total_count: number;
    active_count: number;
    won_count: number;
    lost_count: number;
    total_value: number;
    primary_count: number;
    primary_value: number;
  };

  // Notas y Comunicación
  notes?: string;
  tags?: string[];

  // Test mode flag (Lead.is_test BooleanField column). True = lead de prueba,
  // queda oculto del listado por defecto y no dispara automations/asignación.
  is_test?: boolean;

  // Metadata
  createdAt: string;
  updatedAt: string;
  createdBy?: string;

  // Conversión
  convertedToCustomer?: boolean;
  customerId?: string;
  conversionDate?: string;

  // GDPR / Privacy
  consentGiven?: boolean;
  consentDate?: string;
  marketingOptIn?: boolean;

  // Ubicación
  country?: string;
  state?: string;
  city?: string;

  // Datos Demográficos (Sinco integration)
  address?: string;
  date_of_birth?: string; // ISO date
  dateOfBirth?: string;
  marital_status?: 'soltero' | 'casado' | 'union_libre' | 'divorciado' | 'viudo';
  maritalStatus?: string;
  education_level?: 'primaria' | 'secundaria' | 'tecnico' | 'tecnologo' | 'profesional' | 'posgrado';
  educationLevel?: string;
  occupation?: string;
  housing_status?: 'arriendo' | 'propia' | 'familiar';
  housingStatus?: string;
  person_type?: 'N' | 'J';
  neighborhood?: string;

  // Mascotas
  has_pets?: boolean;
  pet_type?: 'dog' | 'cat' | 'both' | 'other';

  // API computed / nested
  projectDetail?: { id: string; name: string; [key: string]: any };
  interaction_count?: number;
  last_contact_date?: string;
  created_at?: string;
}

export interface LeadFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  documentType?: DocumentType;
  documentNumber?: string;
  source: LeadSource;
  sourceDetail?: string;
  interestedProjects?: string[];
  budget?: Partial<LeadBudget>;
  preferences?: Partial<LeadPreferences>;
  notes?: string;
  tags?: string[];
}

export interface LeadFilters {
  status?: LeadStatus[];
  priority?: LeadPriority[];
  source?: LeadSource[];
  assignedTo?: string[];
  leadScore?: { min: number; max: number };
  ai_close_probability?: { min: number; max: number }; // NUEVO
  createdDate?: { from: string; to: string };
  budget?: { min: number; max: number };
  interestedProjects?: string[];
  convertedToCustomer?: boolean;
  tags?: string[];
}

export interface LeadStats {
  total: number;
  byStatus: Record<LeadStatus, number>;
  byPriority: Record<LeadPriority, number>;
  bySource: Record<LeadSource, number>;
  avgLeadScore: number;
  avgCloseProbability?: number; // NUEVO
  conversionRate: number;
  avgTimeToConversion: number; // días
  totalValue: number; // Valor total de leads ganados
}

// ========================================
// TIPOS DERIVADOS (Para casos específicos)
// ========================================

/** Lead summary - campos mínimos para listas */
export type LeadSummary = Pick<
  Lead,
  'id' | 'leadNumber' | 'firstName' | 'lastName' | 'email' | 'phone' |
  'status' | 'priority' | 'leadScore' | 'source' | 'assignedTo' | 'createdAt'
>;

/** Lead para listados con info básica + scoring */
export type LeadListItem = Pick<
  Lead,
  'id' | 'leadNumber' | 'firstName' | 'lastName' | 'email' | 'phone' |
  'status' | 'priority' | 'source' | 'leadScore' | 'ai_close_probability' |
  'assignedTo' | 'interestedProjects' | 'lastContactDate' | 'createdAt'
>;

/** Lead sin campos de solo lectura (para crear) */
export type LeadCreateInput = Omit<
  Lead,
  'id' | 'leadNumber' | 'createdAt' | 'updatedAt' | 'createdBy' |
  'ai_close_probability' | 'ai_insights' | 'conversion_probability_factors' |
  'quoteCount' | 'totalQuoteValue' | 'lastQuoteDate' | 'interactionCount' |
  'lastInteractionDate' | 'convertedToCustomer' | 'customerId' | 'conversionDate'
>;

/** Lead para actualizaciones (todos los campos opcionales excepto ID) */
export type LeadUpdateInput = Partial<Omit<Lead, 'id' | 'leadNumber' | 'createdAt' | 'updatedAt'>>;

/** Lead con campos requeridos para display en UI */
export interface LeadDisplayData {
  id: string;
  name: string; // firstName + lastName combined
  email: string;
  phone: string;
  status: LeadStatus;
  priority: LeadPriority;
  leadScore: number;
  source: LeadSource;
}

// ========================================
// PRIORITY LEADS (Dashboard Asesor)
// ========================================

/** Datos de cotización para Priority Lead */
export interface PriorityLeadQuoteData {
  quote_number: string;
  quote_id: string;
  total: number;
  status: string;
  sent_date: string | null;
  last_viewed_at: string | null;
  is_live: boolean;
  view_count: number;
  engagement_score: number;
  time_spent_seconds: number;
  exit_section: string | null;
  unit_name: string | null;
  contact_requested?: boolean;
}

/** Recomendación de acción para Priority Lead */
export interface PriorityLeadRecommendation {
  action: string;
  reason: string;
  action_type: 'call' | 'proposal' | 'quote' | 'followup' | 'resend' | 'custom';
  priority: 'critical' | 'urgent' | 'high' | 'medium' | 'low';
  icon: string;
}

/** Info de etapa actual */
export interface PriorityLeadStageInfo {
  id: string;
  name: string;
  semantic_id: string;
  color: string;
}

/** Propiedad de interés */
export interface PriorityLeadPropertyInfo {
  id: string;
  name: string;
  display: string;
}

/** Priority Lead - Lead con datos de engagement y recomendación */
export interface PriorityLead {
  id: string;
  lead_number: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone: string;
  status: LeadStatus;
  priority: LeadPriority;
  lead_score: number;
  ai_close_probability: number | null;
  last_contact_date: string | null;
  next_follow_up_date: string | null;
  total_quote_value: number | null;
  quote_count: number;
  // Computed fields
  quote_data: PriorityLeadQuoteData | null;
  recommendation: PriorityLeadRecommendation;
  priority_level: 'critical' | 'urgent' | 'high' | 'medium' | 'low';
  current_stage_info: PriorityLeadStageInfo | null;
  property_of_interest: PriorityLeadPropertyInfo | null;
}

/** Respuesta del endpoint priority_leads */
export interface PriorityLeadsResponse {
  leads: PriorityLead[];
  total_priority_leads: number;
  showing: number;
  priority_counts: {
    critical: number;
    urgent: number;
    high: number;
    medium: number;
  };
}
